import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { ScrollMapsSidebar } from '../ScrollMapsSidebar';
import { ScrollMapsData, ScrollMapsSidebarNetworkRequestSuccessCallback } from '../types';
import { EngagementMapsState, SegmentData } from '../../Components/types';
import { ScrollMapsFacet } from '../utils/facetdata';

export interface ScrollMapsSidebarContainerProps {
  /**
   * Whether the page is considered to have internal scrolling elements. We should display a warning if so before
   * rendering data.
   */
  isInternalScrolling: boolean;
  /**
   * The state of the toggle for whether Page Fold data is shown.
   */
  medianPageFoldToggleChecked: boolean;
  /**
   * A callback provided when the user wishes to toggle whether Page Fold data is shown.
   */
  onMedianPageFoldToggle: (checked: boolean) => void;
  /**
   * A callback for handling navigating back to the segment definition upon click
   */
  navigateToSegment: () => void;
  /**
   * A callback function to place the placemaps. Used after any  dialogs are acknowledged.
   */
  placeScrollMaps: (
    scrollMapsFacet: ScrollMapsFacet,
    successCallback: ScrollMapsSidebarNetworkRequestSuccessCallback,
  ) => void;
}

export type ScrollMapsSidebarContainerRefMethods = {
  setLoadingState: () => void;
  setScrollMapsFacetData: (scrollData: ScrollMapsFacet) => void;
  handleFetchError: () => void;
};

/**
 * The Parent container for the Scroll Maps sidebar responsible for controlling the loading state.
 * ScrollMapsSideBar handles rendering the UI.
 */
export const ScrollMapsSidebarContainer: React.ForwardRefExoticComponent<
  ScrollMapsSidebarContainerProps & React.RefAttributes<ScrollMapsSidebarContainerRefMethods>
> = forwardRef<ScrollMapsSidebarContainerRefMethods, ScrollMapsSidebarContainerProps>(
  (
    {
      isInternalScrolling,
      medianPageFoldToggleChecked,
      onMedianPageFoldToggle,
      navigateToSegment,
      placeScrollMaps,
    },
    ref,
  ) => {
    const [dataState, setDataState] = useState<EngagementMapsState>('to_load');
    const [scrollMapsData, setScrollMapsData] = useState<ScrollMapsData | null>(null);
    const [scrollMapsFacet, setScrollMapsFacet] = useState<ScrollMapsFacet | null>(null);
    const [segmentData, setSegmentData] = useState<SegmentData | null>(null);
    const [pageFoldToggleChecked, setPageFoldToggleChecked] = useState(medianPageFoldToggleChecked);
    const [internalScrollingAckNeeded, setInternalScrollingAckNeeded] =
      useState<boolean>(isInternalScrolling);

    useImperativeHandle(ref, () => ({
      setLoadingState() {
        setDataState('loading');
      },
      setScrollMapsFacetData(scrollData: ScrollMapsFacet) {
        setScrollMapsFacet(scrollData);
      },
      handleFetchError() {
        setDataState('network_error');
      },
    }));

    useEffect(() => {
      if (internalScrollingAckNeeded && scrollMapsFacet) {
        if ((scrollMapsFacet.internalScrollSelectors?.length || 0) === 0) {
          // We don't have enough data to display an internal scroll maps. Warn user.
          setDataState('warning_displayed');
        } else {
          // We have all enough data to display an internal scroll map correctly
          setInternalScrollingAckNeeded(false);
        }
      }
    }, [scrollMapsFacet]);

    useEffect(() => {
      if (!internalScrollingAckNeeded && scrollMapsFacet) {
        placeScrollMaps(scrollMapsFacet, placeScrollMapsSuccess);
      }
    }, [internalScrollingAckNeeded, scrollMapsFacet]);

    function placeScrollMapsSuccess(segmentData: SegmentData, scrollMapsData: ScrollMapsData) {
      setSegmentData(segmentData);
      setScrollMapsData(scrollMapsData);
      setDataState(scrollMapsData.pageViews === 0 ? 'no_data' : 'data');
    }

    function handleShow() {
      setInternalScrollingAckNeeded(false);
    }

    return (
      // eslint-disable-next-line @fullstory/annotate-react/data-component
      <ScrollMapsSidebar
        dataState={dataState}
        scrollMapsData={scrollMapsData}
        segmentData={segmentData}
        navigateToSegment={navigateToSegment}
        medianPageFoldToggleChecked={pageFoldToggleChecked}
        onMedianPageFoldToggle={checked => {
          setPageFoldToggleChecked(checked);
          onMedianPageFoldToggle(checked);
        }}
        onInternalScrollWarningButtonClick={handleShow}
      />
    );
  },
);
// Need to set name manually due to use using forwardRef()
ScrollMapsSidebarContainer.displayName = 'ScrollMapsSidebarContainer';
